import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, Lock, Gift, Tag, Ticket, Clock, CheckCircle2, ExternalLink, X, KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const usd = (c) => `$${((c || 0) / 100).toFixed(c % 100 === 0 ? 0 : 2)}`;

const pwdKey = (eid) => `swag-pwd-${eid}`;

function CountdownPill({ openAt }) {
  const [diff, setDiff] = useState(() => Math.max(0, new Date(openAt) - new Date()));
  useEffect(() => {
    const t = setInterval(() => setDiff(Math.max(0, new Date(openAt) - new Date())), 1000);
    return () => clearInterval(t);
  }, [openAt]);
  if (diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return (
    <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-1.5 text-amber-500 text-sm font-mono" data-testid="swag-countdown">
      <Clock className="h-4 w-4" /> {d}d {h}h {m}m {s}s
    </div>
  );
}

export default function SwagBagPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [gate, setGate] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [activeOffer, setActiveOffer] = useState(null);
  const [recentClaim, setRecentClaim] = useState(null);
  const [pwdInput, setPwdInput] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  const passwordHeader = () => {
    const stored = localStorage.getItem(pwdKey(eventId));
    return stored ? { 'X-Swag-Password': stored } : {};
  };

  const allHeaders = () => ({ ...headers, ...passwordHeader() });

  useEffect(() => {
    if (!token) {
      navigate(`/login?next=${encodeURIComponent(`/swag-bag/${eventId}`)}`);
      return;
    }
    fetchAll();
    // eslint-disable-next-line
  }, [token, eventId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const g = await axios.get(`${API}/swag-bag/event/${eventId}/gate`, { headers: allHeaders() });
      setGate(g.data);
      if (g.data.is_open) {
        const o = await axios.get(`${API}/swag-bag/event/${eventId}/offers`, { headers: allHeaders() });
        setOffers(o.data.offers || []);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load swag bag');
    } finally { setLoading(false); }
  };

  const tryUnlock = async () => {
    if (!pwdInput.trim()) return;
    setUnlocking(true);
    try {
      await axios.post(`${API}/swag-bag/event/${eventId}/unlock`, { password: pwdInput.trim() }, { headers });
      localStorage.setItem(pwdKey(eventId), pwdInput.trim());
      toast.success('Unlocked!');
      setPwdInput('');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Incorrect password');
    } finally { setUnlocking(false); }
  };

  const claim = async (offer) => {
    setClaiming(offer.offer_id);
    try {
      const res = await axios.post(`${API}/swag-bag/offer/${offer.offer_id}/claim`, {}, { headers: allHeaders() });
      setRecentClaim(res.data);
      setOffers(prev => prev.map(o => o.offer_id === offer.offer_id ? res.data.offer : o).map(o => (
        o.offer_id === offer.offer_id ? { ...o, my_claim: res.data.claim } : o
      )));
      toast.success('Claimed! Your redeem code is ready.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not claim');
    } finally { setClaiming(null); }
  };

  if (loading) return <div className="min-h-screen pt-20 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;

  if (!gate) return null;

  const ev = gate.event || {};

  // ===== GATE (closed) =====
  if (!gate.is_open) {
    return (
      <div className="min-h-screen bg-background pt-20 pb-20" data-testid="swag-bag-gate">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 space-y-6">
          {ev.image_url && <div className="aspect-[3/1] rounded-2xl overflow-hidden border border-border"><img src={ev.image_url} alt="" className="w-full h-full object-cover" /></div>}
          <div className="text-center space-y-3">
            <Lock className="h-10 w-10 mx-auto text-muted-foreground/40" />
            <h1 className="text-3xl sm:text-4xl font-bold">{ev.name} <span className="text-indigo-500">· Swag Bag</span></h1>
            <p className="text-sm text-muted-foreground">{gate.gate_message || `Opens automatically when the event begins.`}</p>
            {gate.open_at && <CountdownPill openAt={gate.open_at} />}
          </div>

          <Card className="border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-indigo-500/10">
            <CardContent className="p-6">
              <p className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">What’s inside</p>
              <p className="text-4xl font-bold mt-1" data-testid="gate-total-value">{usd(gate.total_value_cents)} <span className="text-base font-normal text-muted-foreground">in goodies</span></p>
              <div className="grid sm:grid-cols-3 gap-3 mt-4">
                <div className="text-center bg-background/40 rounded-lg p-3">
                  <Tag className="h-5 w-5 mx-auto text-emerald-400 mb-1" />
                  <p className="text-2xl font-bold">{usd(gate.discount_value_cents)}</p>
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wide">in discounts</p>
                </div>
                <div className="text-center bg-background/40 rounded-lg p-3">
                  <Gift className="h-5 w-5 mx-auto text-indigo-400 mb-1" />
                  <p className="text-2xl font-bold">{usd(gate.giveaway_value_cents)}</p>
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wide">in giveaways</p>
                </div>
                <div className="text-center bg-background/40 rounded-lg p-3">
                  <Ticket className="h-5 w-5 mx-auto text-amber-400 mb-1" />
                  <p className="text-2xl font-bold">{gate.sponsor_count}</p>
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wide">sponsors</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">Don’t have a ticket yet?</p>
            <Link to={`/events/${ev.city}/${ev.slug}`}><Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="gate-buy-tickets">Get tickets to {ev.name} →</Button></Link>
          </div>

          {gate.has_gate_passwords && (
            <Card className="border-amber-500/30 bg-amber-500/5" data-testid="gate-password-card">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-amber-500" />
                  <p className="font-semibold">Have a Swag Bag passcode?</p>
                </div>
                <p className="text-xs text-muted-foreground">Enter the code from your Swag Bag card to unlock the bag right now.</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={pwdInput}
                    onChange={e => setPwdInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') tryUnlock(); }}
                    placeholder="e.g. VIP2026"
                    className="flex-1 h-10 px-3 rounded-md border border-border bg-background font-mono uppercase tracking-wider text-sm"
                    data-testid="gate-password-input"
                  />
                  <Button onClick={tryUnlock} disabled={unlocking || !pwdInput.trim()} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="gate-password-submit">
                    {unlocking ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Unlock'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // ===== OPEN (listing) =====
  return (
    <div className="min-h-screen bg-background pt-20 pb-20" data-testid="swag-bag-open">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-emerald-500 font-semibold">Digital Swag Bag</p>
            <h1 className="text-3xl sm:text-4xl font-bold">{ev.name}</h1>
            <p className="text-sm text-muted-foreground">{usd(gate.total_value_cents)} in goodies · {gate.sponsor_count} sponsors</p>
          </div>
          <Link to={`/events/${ev.city}/${ev.slug}`}><Button variant="outline" size="sm">Event page →</Button></Link>
        </div>

        {offers.length === 0 ? (
          <Card><CardContent className="p-10 text-center"><Gift className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" /><p className="text-sm text-muted-foreground">No offers yet — check back soon.</p></CardContent></Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {offers.map(o => {
              const remaining = (o.quantity_total || 0) - (o.quantity_claimed || 0);
              const soldOut = o.sold_out || remaining <= 0;
              const claimedByMe = !!o.my_claim;
              return (
                <Card key={o.offer_id} className="border-border hover:border-indigo-500/50 transition cursor-pointer overflow-hidden" onClick={() => setActiveOffer(o)} data-testid={`swag-offer-${o.offer_id}`}>
                  {o.image_url && <div className="aspect-video bg-muted"><img src={o.image_url} alt={o.title} className="w-full h-full object-cover" /></div>}
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={o.type === 'discount' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500 text-[10px]' : 'bg-indigo-500/10 border-indigo-500/30 text-indigo-500 text-[10px]'}>{o.type === 'discount' ? <Tag className="h-2.5 w-2.5 mr-1 inline" /> : <Gift className="h-2.5 w-2.5 mr-1 inline" />}{o.type}</Badge>
                      {o.value_cents > 0 && <Badge variant="outline" className="text-[10px]">Value {usd(o.value_cents)}</Badge>}
                    </div>
                    <p className="font-semibold leading-tight">{o.title}</p>
                    <p className="text-xs text-muted-foreground">{o.sponsor_name}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{o.description}</p>
                    <div className="flex items-center justify-between pt-1">
                      <p className="text-[11px] text-muted-foreground">{soldOut ? 'Sold out' : `${remaining} left`}</p>
                      {claimedByMe ? (
                        <Badge variant="outline" className="bg-emerald-500/10 border-emerald-500/30 text-emerald-500"><CheckCircle2 className="h-3 w-3 mr-1" />Claimed</Badge>
                      ) : (
                        <Button size="sm" disabled={soldOut || claiming === o.offer_id} onClick={(e) => { e.stopPropagation(); claim(o); }} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-8" data-testid={`claim-${o.offer_id}`}>
                          {claiming === o.offer_id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}{soldOut ? 'Sold out' : 'Claim'}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Detail modal */}
        {activeOffer && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setActiveOffer(null)} data-testid="swag-offer-modal">
            <Card className="max-w-lg w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">{activeOffer.sponsor_name}</p>
                    <h2 className="text-xl font-bold">{activeOffer.title}</h2>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setActiveOffer(null)}><X className="h-4 w-4" /></Button>
                </div>
                {activeOffer.image_url && <img src={activeOffer.image_url} alt="" className="w-full rounded-md" />}
                <p className="text-sm">{activeOffer.description}</p>
                {activeOffer.my_claim ? (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 space-y-2">
                    <p className="text-xs uppercase font-semibold text-emerald-400">Your redeem code</p>
                    <p className="font-mono text-2xl font-bold text-emerald-400" data-testid="claim-code">{activeOffer.my_claim.redeem_code}</p>
                    {activeOffer.redeem_instructions && <p className="text-xs whitespace-pre-line">{activeOffer.redeem_instructions}</p>}
                    {activeOffer.redeem_link && <a href={activeOffer.redeem_link} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="w-full">Open redeem link <ExternalLink className="h-3 w-3 ml-1" /></Button></a>}
                  </div>
                ) : (
                  <Button onClick={() => claim(activeOffer)} disabled={activeOffer.sold_out} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="modal-claim-btn">
                    {claiming === activeOffer.offer_id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}{activeOffer.sold_out ? 'Sold out' : 'Claim this offer'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
